// ComunicacionHub — /admin/comunicacion
// Portada tipo "agencia de comunicación que contraté" (visión de Nico, 26-jul-2026):
// - Los 3 agentes como tarjetas con su estado REAL (último run desde agentes_runs,
//   piezas esperando OK, próxima corrida).
// - Objetivos del trimestre (app_config.objetivos_90d) con progreso real medido
//   contra pedidos/clientes/empresas/seguidores de la DB.
// - Cola unificada "esperan tu OK" (plan semanal + posts IG + piezas B2B).
// - Banco de ángulos: lo que web-scan-comunicacion extrajo de la web entera.
// Nota: piezas_b2b y banco_angulos llegan con la migración 20260726180000 —
// hasta correrla, esas secciones muestran su empty state (queries con catch).

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { AGENTES_COM, COM_CSS, fmtRun } from '../../components/comunicacion/ComunicacionUI'
import { FlujoStepper, CasoActualCard, cargarEstadoFlujo, FLUJO_CSS } from '../../components/comunicacion/FlujoRedes'

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL

// Fallback si app_config.objetivos_90d no está cargado todavía (mismos números aprobados)
const OBJETIVOS_DEFAULT = {
  fin: '2026-10-24',
  targets: { pedidos_mes: 50, suscripciones: 20, empresas: 5, seguidores_ig: 1000, seguidores_ig_min: 700 },
  base: { pedidos_mes: 2, suscripciones: 2, empresas: 1, seguidores_ig: 62 },
  pauta: { usd_dia: 8, b2c_pct: 70, b2b_pct: 30 },
}

export default function ComunicacionHub() {
  const [loading, setLoading] = useState(true)
  const [runs, setRuns] = useState({})            // { slug: { at, exito, resumen } }
  const [pend, setPend] = useState({ redes: 0, plan: null, empresas: 0 })
  const [objetivos, setObjetivos] = useState(OBJETIVOS_DEFAULT)
  const [actuales, setActuales] = useState({ pedidos_mes: 0, suscripciones: 0, empresas: 0, seguidores_ig: null })
  const [cola, setCola] = useState([])
  const [banco, setBanco] = useState({ items: [], b2c: 0, b2b: 0, total: 0, existe: true })
  const [escaneando, setEscaneando] = useState(false)
  const [msg, setMsg] = useState(null)
  // Estado del flujo IG (los 5 pasos): la lectura "dónde estamos parados"
  const [flujo, setFlujo] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    try {
      // ── Flujo IG: dónde está parada la semana (stepper + caso actual) ──
      try { setFlujo(await cargarEstadoFlujo()) } catch { /* sin permisos o tablas */ }

      // ── Último run por agente (30 días) ──
      const hace30 = new Date(Date.now() - 30 * 86400000).toISOString()
      const { data: runsData } = await supabase
        .from('agentes_runs')
        .select('agente, created_at, exito, output_summary')
        .gte('created_at', hace30)
        .order('created_at', { ascending: false })
        .limit(120)
      const porAgente = {}
      ;(runsData || []).forEach(r => { if (!porAgente[r.agente]) porAgente[r.agente] = r })
      setRuns(porAgente)

      // ── Pendientes de OK ──
      const { count: postsGenerados } = await supabase
        .from('posts_ig').select('id', { count: 'exact', head: true }).eq('estado', 'generado')
      const { data: plan } = await supabase
        .from('planes_semanales')
        .select('id, semana_inicio, piezas_propuestas, resumen, created_at')
        .eq('estado', 'propuesto').order('created_at', { ascending: false }).limit(1).maybeSingle()

      let piezasB2b = []
      let countB2b = 0
      try {
        const r = await supabase.from('piezas_b2b')
          .select('id, tipo, titulo, created_at').eq('estado', 'propuesto').order('created_at', { ascending: false }).limit(6)
        if (!r.error) { piezasB2b = r.data || []; countB2b = piezasB2b.length }
      } catch { /* tabla aún no migrada */ }

      setPend({ redes: postsGenerados || 0, plan: plan || null, empresas: countB2b })

      // ── Cola unificada ──
      const items = []
      if (plan) items.push({ tipo: 'Plan semanal IG', titulo: `Semana del ${plan.semana_inicio} · ${plan.piezas_propuestas} temas propuestos (sin texto todavía — eso viene después)`, to: '/admin/comunicacion/plan', agente: 'redes' })
      const { data: posts } = await supabase
        .from('posts_ig').select('id, brief, pilar, horario_sugerido').eq('estado', 'generado')
        .order('horario_sugerido', { ascending: true }).limit(6)
      ;(posts || []).forEach(p => items.push({
        tipo: 'Pieza IG lista', titulo: (p.brief || '').split('\n')[0].slice(0, 80) || 'Pieza generada',
        to: '/admin/comunicacion/piezas', agente: 'redes',
      }))
      piezasB2b.forEach(p => items.push({ tipo: `B2B · ${p.tipo.replace(/_/g, ' ')}`, titulo: p.titulo, to: '/admin/comunicacion/empresas', agente: 'empresas' }))
      setCola(items)

      // ── Objetivos: targets desde app_config + actuals reales ──
      const { data: cfgObj } = await supabase.from('app_config').select('value').eq('key', 'objetivos_90d').maybeSingle()
      let obj = OBJETIVOS_DEFAULT
      if (cfgObj?.value) { try { obj = { ...OBJETIVOS_DEFAULT, ...JSON.parse(cfgObj.value) } } catch { /* JSON inválido → default */ } }
      setObjetivos(obj)

      // Regla dura del proyecto: NUNCA `${mes}-31` — usar new Date(y, m, 1).
      const hoy = new Date()
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString()
      const [pedidosR, suscR, empR, igR] = await Promise.all([
        supabase.from('pedidos').select('id', { count: 'exact', head: true })
          .eq('es_prueba', false).not('pagado_at', 'is', null).gte('created_at', inicioMes),
        supabase.from('clientes').select('id', { count: 'exact', head: true })
          .eq('estado', 'activa').eq('es_prueba', false),
        supabase.from('empresas').select('id', { count: 'exact', head: true })
          .eq('estado_pipeline', 'activo').is('archivada_at', null),
        supabase.from('cuenta_ig_metricas_diarias').select('followers_count, fecha')
          .order('fecha', { ascending: false }).limit(1).maybeSingle(),
      ])
      setActuales({
        pedidos_mes: pedidosR.count || 0,
        suscripciones: suscR.count || 0,
        empresas: empR.count || 0,
        seguidores_ig: igR.data?.followers_count ?? null,
      })

      // ── Banco de ángulos ──
      try {
        const [lista, cB2c, cB2b] = await Promise.all([
          supabase.from('banco_angulos').select('id, tipo, ref, titulo, angulo, audiencia, estado, updated_at')
            .eq('estado', 'disponible').order('updated_at', { ascending: false }).limit(10),
          supabase.from('banco_angulos').select('id', { count: 'exact', head: true }).eq('estado', 'disponible').in('audiencia', ['b2c', 'ambas']),
          supabase.from('banco_angulos').select('id', { count: 'exact', head: true }).eq('estado', 'disponible').in('audiencia', ['b2b', 'ambas']),
        ])
        if (lista.error) setBanco(b => ({ ...b, existe: false }))
        else setBanco({ items: lista.data || [], b2c: cB2c.count || 0, b2b: cB2b.count || 0, total: (lista.data || []).length, existe: true })
      } catch { setBanco(b => ({ ...b, existe: false })) }
    } catch (e) {
      console.error('ComunicacionHub cargar:', e)
    } finally {
      setLoading(false)
    }
  }

  async function escanearWeb() {
    if (!confirm('¿Escanear toda la web ahora?\n\nEl analista recorre limpack.uy + tienda.limpack.uy (kits, productos, barrios, páginas especiales) y actualiza el banco de ángulos. Toma 1-2 minutos.')) return
    setEscaneando(true); setMsg(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Sin sesión activa')
      const r = await fetch(`${SUPA_URL}/functions/v1/web-scan-comunicacion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'hub_manual' }),
      })
      const json = await r.json()
      if (!r.ok || json.error) throw new Error(json.error || `HTTP ${r.status}`)
      setMsg({ tipo: 'ok', texto: `✓ Web escaneada: ${json.insertados} ángulos nuevos, ${json.actualizados} actualizados.` })
      await cargar()
    } catch (e) {
      setMsg({ tipo: 'err', texto: e.message })
    } finally {
      setEscaneando(false)
    }
  }

  // Último run "del puesto": el más reciente entre sus edge functions
  function ultimoRunDe(agente) {
    let best = null
    for (const slug of agente.runSlugs) {
      const r = runs[slug]
      if (r && (!best || r.created_at > best.created_at)) best = r
    }
    return best
  }

  const pendientesDe = { redes: (pend.redes || 0) + (pend.plan ? 1 : 0), empresas: pend.empresas, folleteria: 0 }

  return (
    <AdminLayout title="Comunicación">
      <div className="com-wrap">

        {/* CABECERA AGENCIA */}
        <div className="com-hero">
          <div className="com-hero-avatar">📣</div>
          <div className="com-hero-body">
            <div className="com-hero-eyebrow">TU AGENCIA DE COMUNICACIÓN</div>
            <h1 className="com-hero-nombre">Comunicación Limpack</h1>
            <div className="com-hero-rol">3 agentes trabajando para vos · vos solo aprobás</div>
            <p className="com-hero-desc">
              Redes piensa y publica el Instagram B2C. Empresas prospecta comercios y administradoras por Google y mail
              (B2B no va por IG). Folletería imprime y trackea con QR. Todo lo que esperan de vos está en la cola de abajo.
            </p>
            <div className="com-hero-meta">
              <span>💰 Pauta: USD {objetivos.pauta?.usd_dia ?? 8}/día · {objetivos.pauta?.b2c_pct ?? 70}% B2C / {objetivos.pauta?.b2b_pct ?? 30}% B2B</span>
              <span>· ✂️ Regla de corte: canal sin pedidos en 30 días, se corta</span>
            </div>
          </div>
        </div>

        {msg && <div className={`com-msg ${msg.tipo === 'ok' ? 'com-msg-ok' : 'com-msg-err'}`}>{msg.texto}</div>}

        {/* EL FLUJO DE INSTAGRAM EN 5 PASOS + DÓNDE ESTAMOS PARADOS.
            Es lo primero que se ve: responde "qué aprobé, qué falta y quién lo hace". */}
        <FlujoStepper pasoActual={flujo?.paso || 1} />
        {flujo && <CasoActualCard est={flujo} onMsg={setMsg} onDone={cargar} />}

        {/* LOS 3 AGENTES */}
        <div className="comhub-agentes">
          {Object.values(AGENTES_COM).map(a => {
            const run = ultimoRunDe(a)
            const nPend = pendientesDe[a.slug] || 0
            return (
              <Link key={a.slug} to={a.to} className="comhub-agente">
                <div className="comhub-agente-top">
                  <span className="comhub-agente-emoji">{a.emoji}</span>
                  {nPend > 0 && <span className="com-pill com-pill-gold">{nPend} esperan tu OK</span>}
                  {nPend === 0 && <span className="com-pill com-pill-gray">al día</span>}
                </div>
                <div className="comhub-agente-nombre">{a.nombre}</div>
                <div className="comhub-agente-rol">{a.rol}</div>
                <p className="comhub-agente-desc">{a.desc}</p>
                <div className="comhub-agente-foot">
                  {a.runSlugs.length > 0 ? (
                    <>
                      <span className={`comhub-dot ${run ? (run.exito ? 'ok' : 'err') : 'off'}`} />
                      <span>Último run: <strong>{fmtRun(run?.created_at)}</strong></span>
                    </>
                  ) : (
                    <span>📦 Manual — imprimís y repartís vos</span>
                  )}
                </div>
                <div className="comhub-agente-cron">⏱ {a.crones}</div>
              </Link>
            )
          })}
        </div>

        {/* OBJETIVOS DEL TRIMESTRE — progreso real */}
        <section className="com-card">
          <div className="com-card-head">
            <h2 className="com-card-title">🎯 Norte a 90 días</h2>
            <span className="com-card-sub">Aprobado 26-jul · vence {objetivos.fin} · medido contra la DB real, no contra deseos</span>
          </div>
          <div className="comhub-objetivos">
            <Objetivo label="Pedidos / mes" actual={actuales.pedidos_mes} base={objetivos.base?.pedidos_mes} target={objetivos.targets?.pedidos_mes} sub="pagados este mes" />
            <Objetivo label="Suscripciones activas" actual={actuales.suscripciones} base={objetivos.base?.suscripciones} target={objetivos.targets?.suscripciones} sub="clientes activa" />
            <Objetivo label="Empresas activas" actual={actuales.empresas} base={objetivos.base?.empresas} target={objetivos.targets?.empresas} sub="pipeline: activo" />
            <Objetivo
              label="Seguidores IG"
              actual={actuales.seguidores_ig ?? objetivos.base?.seguidores_ig}
              base={objetivos.base?.seguidores_ig}
              target={objetivos.targets?.seguidores_ig}
              sub={actuales.seguidores_ig === null ? 'sin pull todavía (base 62)' : 'meta 700-1.000'}
            />
          </div>
          <div className="comhub-kpi-nota">
            KPIs por canal (30d): IG 3 posts/sem +200 seg · Ads B2C 15 pedidos CPA&lt;10 · Ads B2B 8 cotizaciones ·
            GBP 10 clicks/sem · Folletos 500 impresos 15 escaneos · WA respuesta &lt;2h · Email pre-entrega.
            El detalle vive en el playbook (app_config.playbook_campania).
          </div>
        </section>

        {/* COLA UNIFICADA */}
        <section className="com-card" style={{ borderLeft: '4px solid #C5A059' }}>
          <div className="com-card-head">
            <h2 className="com-card-title">✋ Esperan tu OK</h2>
            <span className="com-card-sub">{cola.length} pendiente{cola.length !== 1 ? 's' : ''} en toda la agencia</span>
          </div>
          {loading && <div className="com-empty">Cargando…</div>}
          {!loading && cola.length === 0 && (
            <div className="com-empty"><strong>Nada pendiente.</strong>Cuando un agente proponga algo, aparece acá.</div>
          )}
          {!loading && cola.length > 0 && (
            <div className="comhub-cola">
              {cola.map((c, i) => (
                <Link key={i} to={c.to} className="comhub-cola-item">
                  <span className={`com-pill ${c.agente === 'redes' ? 'com-pill-teal' : 'com-pill-gold'}`}>{c.tipo}</span>
                  <span className="comhub-cola-titulo">{c.titulo}</span>
                  <span className="comhub-cola-go">revisar →</span>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* BANCO DE ÁNGULOS */}
        <section className="com-card">
          <div className="com-card-head">
            <div>
              <h2 className="com-card-title">🧭 Banco de ángulos · toda la web, lista para contar</h2>
              <span className="com-card-sub">
                El analista recorre limpack.uy + tienda.limpack.uy (kits, productos, barrios, /empresas, /porteros, /cuadra…)
                y deja acá los ángulos. Los agentes eligen de acá: nunca más "¿de qué hablo?".
              </span>
            </div>
            <button onClick={escanearWeb} disabled={escaneando || !banco.existe} className="com-btn com-btn-primary">
              {escaneando ? 'Escaneando la web…' : '🔍 Escanear web ahora'}
            </button>
          </div>
          {!banco.existe && (
            <div className="com-msg com-msg-err">
              La tabla banco_angulos todavía no existe: corré la migración 20260726180000 y deployá web-scan-comunicacion.
            </div>
          )}
          {banco.existe && (
            <>
              <div className="comhub-banco-stats">
                <span className="com-pill com-pill-teal">{banco.b2c} disponibles B2C</span>
                <span className="com-pill com-pill-gold">{banco.b2b} disponibles B2B</span>
              </div>
              {banco.items.length === 0 ? (
                <div className="com-empty"><strong>Banco vacío.</strong>Apretá "Escanear web ahora" (o esperá el cron del lunes 06:00).</div>
              ) : (
                <div className="comhub-banco">
                  {banco.items.map(a => (
                    <div key={a.id} className="comhub-angulo">
                      <div className="comhub-angulo-head">
                        <span className="com-pill com-pill-gray">{a.tipo}</span>
                        <span className={`com-pill ${a.audiencia === 'b2b' ? 'com-pill-gold' : a.audiencia === 'ambas' ? 'com-pill-green' : 'com-pill-teal'}`}>{a.audiencia}</span>
                      </div>
                      <div className="comhub-angulo-titulo">{a.titulo}</div>
                      <div className="comhub-angulo-texto">{a.angulo}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        {/* MODO AVANZADO — el taller fino que antes vivía en "Web & Marketing".
            No compite con el flujo simple: son herramientas, no la puerta de entrada. */}
        <section className="com-card" style={{ background: '#F8F6F1' }}>
          <div className="com-card-head">
            <div>
              <h2 className="com-card-title">🧰 Modo avanzado (el taller)</h2>
              <span className="com-card-sub">Para el día a día no las necesitás — el flujo de arriba alcanza. Acá está la herramienta fina.</span>
            </div>
          </div>
          <div className="comhub-avanzado">
            <Link to="/admin/marketing" className="comhub-av-link"><span>🏭</span> Taller de piezas<em>editar texto/imagen a fondo, componer, timeline</em></Link>
            <Link to="/admin/marketing-agent" className="comhub-av-link"><span>🎩</span> Chat con el Gerente<em>pedirle una pieza puntual por brief</em></Link>
            <Link to="/admin/calendario-marketing" className="comhub-av-link"><span>🗓️</span> Calendario a mano<em>cargar/editar piezas manuales de cualquier canal</em></Link>
            <Link to="/admin/marketing/volante" className="comhub-av-link"><span>📄</span> Builder de volantes<em>material para edificios</em></Link>
            <Link to="/admin/marketing/lotes" className="comhub-av-link"><span>🔗</span> Lotes & QR<em>tracking de papel repartido</em></Link>
            <Link to="/admin/marketing/meta-ads" className="comhub-av-link"><span>💰</span> Meta Ads<em>pauta paga</em></Link>
          </div>
        </section>

      </div>

      <style>{FLUJO_CSS}</style>
      <style>{COM_CSS}</style>
      <style>{`
        .comhub-agentes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
        @media (max-width: 900px) { .comhub-agentes { grid-template-columns: 1fr; } }
        .comhub-agente {
          background: #fff; border: 1px solid #E5E7EB; border-radius: 14px; padding: 16px;
          text-decoration: none; color: inherit; display: flex; flex-direction: column; gap: 6px;
          transition: box-shadow .12s, transform .12s;
        }
        .comhub-agente:hover { box-shadow: 0 6px 18px rgba(26,52,97,.12); transform: translateY(-1px); }
        .comhub-agente-top { display: flex; justify-content: space-between; align-items: center; }
        .comhub-agente-emoji { font-size: 26px; }
        .comhub-agente-nombre { font-size: 15.5px; font-weight: 800; color: #1A3461; }
        .comhub-agente-rol { font-size: 11px; font-weight: 700; color: #00A8B5; text-transform: uppercase; letter-spacing: .04em; }
        .comhub-agente-desc { font-size: 12px; color: #6B7280; line-height: 1.45; margin: 2px 0 0; flex: 1; }
        .comhub-agente-foot { display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: #6B7280; margin-top: 8px; }
        .comhub-agente-foot strong { color: #1A3461; }
        .comhub-agente-cron { font-size: 10.5px; color: #94A3B8; }
        .comhub-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .comhub-dot.ok { background: #22C55E; } .comhub-dot.err { background: #DC2626; } .comhub-dot.off { background: #D1D5DB; }

        .comhub-objetivos { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
        @media (max-width: 900px) { .comhub-objetivos { grid-template-columns: repeat(2, 1fr); } }
        .comhub-obj { border: 1px solid #EDF0F4; border-radius: 10px; padding: 10px 12px; }
        .comhub-obj-label { font-size: 10px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #6B7280; }
        .comhub-obj-nums { display: flex; align-items: baseline; gap: 5px; margin-top: 4px; }
        .comhub-obj-actual { font-size: 22px; font-weight: 900; color: #1A3461; }
        .comhub-obj-target { font-size: 12px; color: #94A3B8; font-weight: 700; }
        .comhub-obj-bar { height: 6px; background: #EEF1F5; border-radius: 999px; margin-top: 8px; overflow: hidden; }
        .comhub-obj-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, #00A8B5, #5EE8F0); }
        .comhub-obj-sub { font-size: 10.5px; color: #94A3B8; margin-top: 5px; }
        .comhub-kpi-nota { margin-top: 12px; font-size: 11px; color: #6B7280; background: #F8F6F1; border-radius: 8px; padding: 8px 12px; line-height: 1.5; }

        .comhub-cola { display: flex; flex-direction: column; gap: 6px; }
        .comhub-cola-item {
          display: flex; align-items: center; gap: 10px; padding: 9px 12px;
          background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 9px;
          text-decoration: none; color: inherit; font-size: 12.5px;
        }
        .comhub-cola-item:hover { border-color: #00A8B5; }
        .comhub-cola-titulo { flex: 1; color: #111827; }
        .comhub-cola-go { font-size: 11px; font-weight: 700; color: #00A8B5; }

        .comhub-banco-stats { display: flex; gap: 8px; margin-bottom: 10px; }
        .comhub-banco { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
        @media (max-width: 800px) { .comhub-banco { grid-template-columns: 1fr; } }
        .comhub-angulo { border: 1px solid #EDF0F4; border-radius: 10px; padding: 10px 12px; }
        .comhub-angulo-head { display: flex; gap: 6px; margin-bottom: 6px; }
        .comhub-angulo-titulo { font-size: 12.5px; font-weight: 800; color: #1A3461; }
        .comhub-angulo-texto { font-size: 12px; color: #4B5563; line-height: 1.45; margin-top: 3px; }

        .comhub-avanzado { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        @media (max-width: 800px) { .comhub-avanzado { grid-template-columns: 1fr; } }
        .comhub-av-link {
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
          padding: 9px 12px; background: #fff; border: 1px solid #E5E7EB; border-radius: 9px;
          text-decoration: none; color: #1A3461; font-size: 12.5px; font-weight: 700;
        }
        .comhub-av-link:hover { border-color: #00A8B5; }
        .comhub-av-link span { font-size: 15px; }
        .comhub-av-link em { display: block; width: 100%; font-style: normal; font-weight: 400; font-size: 10.5px; color: #94A3B8; }
      `}</style>
    </AdminLayout>
  )
}

function Objetivo({ label, actual, base, target, sub }) {
  const a = Number(actual) || 0
  const t = Number(target) || 1
  const pct = Math.max(2, Math.min(100, Math.round((a / t) * 100)))
  return (
    <div className="comhub-obj">
      <div className="comhub-obj-label">{label}</div>
      <div className="comhub-obj-nums">
        <span className="comhub-obj-actual">{a}</span>
        <span className="comhub-obj-target">/ {target} <span style={{ fontWeight: 500 }}>(base {base})</span></span>
      </div>
      <div className="comhub-obj-bar"><div className="comhub-obj-fill" style={{ width: pct + '%' }} /></div>
      <div className="comhub-obj-sub">{sub} · {pct}%</div>
    </div>
  )
}
